import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass as SearchIcon,
  faFilter as FilterIcon,
  faDownload as AddOrdersIcon,
  faChevronDown as KeyboardArrowDownIcon,
  faTruckFast as VehicleIcon,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { useAppSelector, useToggleMenu } from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { getStringifyUrlQueryString } from '~utils/router';
import { isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { getRangesDate } from '~utils/dates';
import { formatterDate } from '~utils/formatter';
import OrdersFiltersMenuV2 from '~components/OrdersFiltersMenuV2';
import AddOrdersActions from '~components/AddOrdersActions';
import MultipleLinearProgresBar from '~components/MultipleLinearProgresBar';
import { PATHS } from '~constants/paths';
import { RouteStateTypes } from '~globals/types/enums';

import { useDashboardVariantV2CarrierContext } from '../../../Context';
import { useDashboardVariantV2CarrierSelectDataContext } from '../../../SelectDataContext';
import { useDashboardVariantV2CarrierCollectFiltersContext } from '../../../CollectFiltersContext';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierInWarehouseFiltersContext } from '../../../InWarehouseFiltersContext';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierInTransitFiltersContext } from '../../../InTransitFiltersContext';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierFinalizedFiltersContext } from '../../../FinalizedFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../PresetsContext';
import { DashboardVariantV2CarrierHeaderExpandMore } from '../../SubComponents';

import {
  ActionsWidgetCardContainer,
  ActionsWidgetCardFieldWithButtonGroup,
  ActionsWidgetCardTextField,
  ActionsWidgetCardButton,
  ActionsWidgetCardIconButton,
  ActionsWidgetCardIconButtonIcon,
} from './styles';

const DashboardVariantV2CarrierActionsWidgetCard = (): ReactElement => {
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuth);

  const {
    activeTab: { currentTab, getIsActiveTab },
    statistics: {
      data: {
        route: {
          totalRoutes,
          totalPendingRoutes,
          totalInProgressRoutes,
          totalCompletedRoutes,
        },
        companyInfo: { quantityGiverCompanies },
      },
    },
    dialogs: {
      importFromFile: { toggleOpen: toggleOpenDialogImportFromFile },
      importFromIntegration: {
        toggleOpen: toggleOpenDialogImportFromIntegration,
      },
      importFromAssociatedCompany: {
        toggleOpen: toggleOpenDialogImportFromAssociatedCompany,
      },
    },
  } = useDashboardVariantV2CarrierContext();

  const selectsData = useDashboardVariantV2CarrierSelectDataContext();

  const filtersCollect = useDashboardVariantV2CarrierCollectFiltersContext();
  const filtersInWarehouse =
    useDashboardVariantV2CarrierInWarehouseFiltersContext();
  const filtersInTransit =
    useDashboardVariantV2CarrierInTransitFiltersContext();
  const filtersFinalized =
    useDashboardVariantV2CarrierFinalizedFiltersContext();

  const { filters: selectedFilter } = useMemo(() => {
    if (getIsActiveTab('finalized')) return filtersFinalized;

    if (getIsActiveTab('inTransit')) return filtersInTransit;

    if (getIsActiveTab('collect')) return filtersCollect;

    return filtersInWarehouse;
  }, [
    filtersCollect,
    filtersFinalized,
    filtersInTransit,
    filtersInWarehouse,
    getIsActiveTab,
  ]);

  const presets = useDashboardVariantV2CarrierPresetsContext();

  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const [
    anchorElOrdersFiltersMenu,
    openOrdersFiltersMenu,
    toggleOpenOrdersFiltersMenu,
  ] = useToggleMenu();

  const [anchorElAddOrdersMenu, openAddOrdersMenu, toggleOpenAddOrdersMenu] =
    useToggleMenu();

  const handleRedirectToNewOrder = useCallback(() => {
    navigate(PATHS.ORDERS.NEW);
  }, [navigate]);

  const handleRedirectToRoutes = useCallback(() => {
    navigate(PATHS.ROUTES.BASE);
  }, [navigate]);

  const hanleRedirectToRoutesByFilters = useCallback(
    ({
      states,
      startDate,
      endDate,
    }: {
      states: RouteStateTypes[];
      startDate: Date;
      endDate: Date;
    }) => {
      navigate(
        getStringifyUrlQueryString({
          url: PATHS.ROUTES.BASE,
          query: {
            states,
            dateFrom: formatterDate(startDate, {
              format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
            }),
            dateTo: formatterDate(endDate, {
              format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
            }),
          },
        }),
      );
    },
    [navigate],
  );

  const handleRedirectToPendingRoutes = useCallback(() => {
    const { startDate, endDate } = getRangesDate({
      start: { operation: 'subtract', amount: 1, unit: 'month' },
    });

    hanleRedirectToRoutesByFilters({
      states: [
        RouteStateTypes.New,
        RouteStateTypes.Assigned,
        RouteStateTypes.Approve,
      ],
      startDate,
      endDate,
    });
  }, [hanleRedirectToRoutesByFilters]);

  const handleRedirectToInProgressRoutes = useCallback(() => {
    const { startDate, endDate } = getRangesDate({
      start: { operation: 'subtract', amount: 1, unit: 'month' },
    });

    hanleRedirectToRoutesByFilters({
      states: [RouteStateTypes.InProgress],
      startDate,
      endDate,
    });
  }, [hanleRedirectToRoutesByFilters]);

  const handleRedirectToCompletedRoutes = useCallback(() => {
    const { startDate, endDate } = getRangesDate({
      start: { operation: 'subtract', amount: 1, unit: 'day' },
    });

    hanleRedirectToRoutesByFilters({
      states: [RouteStateTypes.Finalized, RouteStateTypes.Rejected],
      startDate,
      endDate,
    });
  }, [hanleRedirectToRoutesByFilters]);

  return (
    <ActionsWidgetCardContainer>
      <Stack spacing={1}>
        <Stack spacing={1} direction="row">
          <ActionsWidgetCardFieldWithButtonGroup>
            <ActionsWidgetCardTextField
              label="Nro. Orden / Nro. Tracking / Grupo"
              name="search"
              fullWidth
              value={selectedFilter.filters.search ?? ''}
              onChange={selectedFilter.handleChangeInputFilter}
            />

            <Tooltip title="Buscar..." arrow>
              <span style={{ display: 'flex' }}>
                <ActionsWidgetCardIconButton
                  color="secondary"
                  variant="contained"
                  onClick={selectedFilter.handleApplyFilter}
                  disabled={!selectedFilter.filters.search}
                >
                  <ActionsWidgetCardIconButtonIcon icon={SearchIcon} />
                </ActionsWidgetCardIconButton>
              </span>
            </Tooltip>
          </ActionsWidgetCardFieldWithButtonGroup>

          <Tooltip title="Mas filtros" arrow>
            <ActionsWidgetCardIconButton
              color="primary"
              variant="outlined"
              ref={anchorElOrdersFiltersMenu}
              onClick={toggleOpenOrdersFiltersMenu}
            >
              <ActionsWidgetCardIconButtonIcon icon={FilterIcon} />
            </ActionsWidgetCardIconButton>
          </Tooltip>

          <OrdersFiltersMenuV2
            anchorEl={anchorElOrdersFiltersMenu.current}
            anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'center', vertical: 'top' }}
            open={openOrdersFiltersMenu}
            onClose={toggleOpenOrdersFiltersMenu}
            type={currentTab}
            filters={{
              collect: {
                autocompletesRefs: filtersCollect.autocompletesRefs,
                filters: filtersCollect.filters.filters,
                activeFilters: filtersCollect.filters.filtersActives,
                onChangeFilterInput: filtersCollect.filters.setFilterByKey,
                onResetFilters: filtersCollect.filters.handleResetFilters,
                onApplyFilters: filtersCollect.filters.handleApplyFilter,
                onClearFilters: () =>
                  filtersCollect.filters.handleClearFilters(true),
              },
              inWarehouse: {
                autocompletesRefs: filtersInWarehouse.autocompletesRefs,
                filters: filtersInWarehouse.filters.filters,
                activeFilters: filtersInWarehouse.filters.filtersActives,
                onChangeFilterInput: filtersInWarehouse.filters.setFilterByKey,
                onResetFilters: filtersInWarehouse.filters.handleResetFilters,
                onApplyFilters: filtersInWarehouse.filters.handleApplyFilter,
                onClearFilters: () =>
                  filtersInWarehouse.filters.handleClearFilters(true),
              },
              inTransit: {
                autocompletesRefs: filtersInTransit.autocompletesRefs,
                filters: filtersInTransit.filters.filters,
                activeFilters: filtersInTransit.filters.filtersActives,
                onChangeFilterInput: filtersInTransit.filters.setFilterByKey,
                onResetFilters: filtersInTransit.filters.handleResetFilters,
                onApplyFilters: filtersInTransit.filters.handleApplyFilter,
                onClearFilters: () =>
                  filtersInTransit.filters.handleClearFilters(true),
              },
              finalized: {
                autocompletesRefs: filtersFinalized.autocompletesRefs,
                filters: filtersFinalized.filters.filters,
                activeFilters: filtersFinalized.filters.filtersActives,
                onChangeFilterInput: filtersFinalized.filters.setFilterByKey,
                onResetFilters: filtersFinalized.filters.handleResetFilters,
                onApplyFilters: filtersFinalized.filters.handleApplyFilter,
                onClearFilters: () =>
                  filtersFinalized.filters.handleClearFilters(true),
              },
            }}
            selectsData={selectsData}
            presets={presets}
          />
        </Stack>

        <Stack spacing={1} direction="row" minHeight={41}>
          {!isUserOnlyReader && (
            <>
              <ActionsWidgetCardButton
                variant="contained"
                color="primary"
                startIcon={<FontAwesomeIcon icon={AddOrdersIcon} />}
                endIcon={<FontAwesomeIcon icon={KeyboardArrowDownIcon} />}
                fullWidth
                size="large"
                ref={anchorElAddOrdersMenu}
                onClick={toggleOpenAddOrdersMenu}
              >
                Agregar pedidos
              </ActionsWidgetCardButton>

              <AddOrdersActions.Menu
                anchorEl={anchorElAddOrdersMenu.current}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                open={openAddOrdersMenu}
                onClose={toggleOpenAddOrdersMenu}
                items={{
                  manual: {
                    onAction: handleRedirectToNewOrder,
                  },
                  fromFile: {
                    onAction: () => toggleOpenDialogImportFromFile(true),
                  },
                  fromIntegration: {
                    onAction: () => toggleOpenDialogImportFromIntegration(true),
                  },
                  fromAssociatedCompany: {
                    disabled: quantityGiverCompanies <= 0,
                    onAction: () =>
                      toggleOpenDialogImportFromAssociatedCompany(true),
                  },
                }}
              />
            </>
          )}

          {!isUserOnlyCreatorOrder && (
            <ActionsWidgetCardButton
              variant="outlined"
              color="primary"
              startIcon={<FontAwesomeIcon icon={VehicleIcon} />}
              fullWidth
              size="large"
              onClick={handleRedirectToRoutes}
            >
              Ver lista de rutas
            </ActionsWidgetCardButton>
          )}
        </Stack>
      </Stack>

      {!isUserOnlyCreatorOrder && (
        <DashboardVariantV2CarrierHeaderExpandMore.Collapse>
          <Stack
            spacing={1}
            marginTop={1.5}
            minHeight={109}
            justifyContent="flex-end"
          >
            <Typography component="h6" variant="h5" fontWeight="bold">
              {`${totalRoutes} Rutas`}
            </Typography>

            <MultipleLinearProgresBar
              items={[
                {
                  label: 'A realizar',
                  value: totalPendingRoutes,
                  color: '#00F4E5',
                  onClick: handleRedirectToPendingRoutes,
                },
                {
                  label: 'En tránsito',
                  value: totalInProgressRoutes,
                  color: '#5618B6',
                  onClick: handleRedirectToInProgressRoutes,
                },
                {
                  label: 'Completas',
                  value: totalCompletedRoutes,
                  color: '#388E3C',
                  onClick: handleRedirectToCompletedRoutes,
                },
              ]}
              size="small"
              showTotal={false}
            />
          </Stack>
        </DashboardVariantV2CarrierHeaderExpandMore.Collapse>
      )}
    </ActionsWidgetCardContainer>
  );
};

export default DashboardVariantV2CarrierActionsWidgetCard;
